/**
 * veraltet, nach Welle 2 entfernen.
 *
 * B-GTCC-UI-ADOPT1: the JSX-producing pieces of this module (`FleetDot`,
 * `FleetFreshBadge`, `NotMeasurable`, `FleetDash`, `FleetSparkline`,
 * `FleetMeter`, `FleetDiskCell`, `FleetRow`, `FleetNote`) are now thin
 * wrappers around the canonical `ui/primitives.tsx` / `ui/charts.tsx`
 * components delivered by B-GTCC-UI1 — no call site in this repo still
 * imports them (every migrated view builds its own small local adapter
 * directly on `ui/primitives`/`ui/charts`, see `HostTable.tsx`), but the
 * names stay as a compatibility shim for any other in-flight lane that may
 * still reference them by name. They are kept only for that reason and
 * should be deleted, not merely re-pointed, once every lane has migrated
 * (Welle 2).
 *
 * The tones, formatters and layout constants below (`FleetTone`,
 * `toneOfStatus`, `toneOfVerdict`, `toneColor`, `formatWholePercent`,
 * `formatDecimalGb`, `formatGib`, `formatMbps`, `formatInteger`, `formatMs`,
 * `formatMinutes`, `FORECAST_HORIZON_DAYS`, `FORECAST_WARN_DAYS`,
 * `GTCC_VISUALLY_HIDDEN`, `PANEL_HEADING_STYLE`) have no equivalent in
 * `ui/primitives.tsx` — it deals in pre-formatted strings and CSS classes
 * only, not in DTOs, locales or i18n keys — so they stay real, actively
 * imported domain code, not part of the re-export shim.
 *
 * Honesty rules (Owner P8/P9/P10): no value is ever formatted into existence.
 * `null` renders as "nicht messbar" (grey) or as the em dash the reference
 * shows, never as 0 and never as a guess. Age comes from the payload; nothing
 * here reads a clock.
 */
import { useLocale } from "@genius-tracker/i18n/react";
import { activeLocale, type Locale } from "@genius-tracker/i18n";
import type { CSSProperties, ReactNode } from "react";

import type { FleetDiskDto, FleetStatus, FleetVerdict } from "../../../api/fleet.js";
import { Sparkline, type ChartTone } from "../../../ui/charts.js";
import { Dot, DiskCell, FreshBadge, Meter, Row } from "../../../ui/primitives.js";

/* ----------------------------------------------------------------- tones */

export type FleetTone = "ok" | "warn" | "crit" | "info" | "muted";

const TONE_COLOR: Readonly<Record<FleetTone, string>> = {
  ok: "var(--gt-sys-success)",
  warn: "var(--gt-sys-warning)",
  crit: "var(--gt-sys-danger)",
  info: "var(--gt-sys-info)",
  muted: "var(--gt-sys-text-muted)",
};

/** `status` of the API convention → the dot/stroke colour of the reference. */
export function toneOfStatus(status: FleetStatus): FleetTone {
  if (status === "healthy") return "ok";
  if (status === "warning" || status === "stale") return "warn";
  if (status === "critical") return "crit";
  return "muted";
}

/** `verdict` of the host-detail table → the same five tones. */
export function toneOfVerdict(verdict: FleetVerdict): FleetTone {
  if (verdict === "ok") return "ok";
  if (verdict === "warn") return "warn";
  if (verdict === "crit") return "crit";
  if (verdict === "info") return "info";
  return "muted";
}

export function toneColor(tone: FleetTone): string {
  return TONE_COLOR[tone];
}

/* ------------------------------------------------------------ formatters */

/**
 * `24 %` — whole percent, the way the reference writes it (`24 %`, `82 %`),
 * spaced by the locale. `null` → the caller decides between em dash and
 * "nicht messbar"; this helper never invents a zero.
 */
export function formatWholePercent(value: number, locale: Locale = activeLocale()): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(value / 100);
}

/** `142 GB` — decimal gigabytes, the unit the reference uses for "Used". */
export function formatDecimalGb(bytes: number, locale: Locale = activeLocale()): string {
  const gb = bytes / 1_000_000_000;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(gb)} GB`;
}

/** `9,3 GiB` — binary gibibytes, the unit the reference uses for "frei"/"Free". */
export function formatGib(bytes: number, locale: Locale = activeLocale()): string {
  const gib = bytes / 1024 ** 3;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(gib)} GiB`;
}

/** `120 Mbps` — the network unit of the reference, from bits per second. */
export function formatMbps(bps: number, locale: Locale = activeLocale()): string {
  const mbps = bps / 1_000_000;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(mbps);
}

/** Whole numbers: milliseconds, days, host counts — the locale's digits. */
export function formatInteger(value: number, locale: Locale = activeLocale()): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

export function formatMs(ms: number, locale: Locale = activeLocale()): string {
  return formatInteger(ms, locale);
}

export function formatMinutes(seconds: number, locale: Locale = activeLocale()): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(seconds / 60);
}

/**
 * The forecast horizon of the reference: 54 and 88 days are written out, a
 * forecast beyond 90 days is written "> 90 Tage bis voll"
 * (`GTCC_REFERENCE_2026-09-01.html:229,231`). Not a threshold of our own — the
 * warn colouring stays with `disk.daysToFull` and the API status.
 */
export const FORECAST_HORIZON_DAYS = 90;

/** Owner decision P10: a forecast under 30 days is shown as a warning. */
export const FORECAST_WARN_DAYS = 30;

/* ------------------------------------------------------ re-export shim */

/** Re-export of `ui/primitives.tsx`'s `Dot`, wrapped for `data-testid`. */
export function FleetDot(props: { tone: FleetTone; testId?: string }): ReactNode {
  return (
    <span data-testid={props.testId}>
      <Dot tone={props.tone} />
    </span>
  );
}

/**
 * Re-export of `ui/primitives.tsx`'s `FreshBadge`, retaining the cockpit test
 * hooks while the shared primitive derives age from the real measurement.
 */
export function FleetFreshBadge(props: {
  seconds?: number | undefined;
  measuredAt: string | null;
  stale?: boolean;
  testId?: string;
}): ReactNode {
  const stale = props.stale === true;
  return (
    <span data-testid={props.testId} data-stale={stale ? "true" : "false"}>
      <FreshBadge measuredAt={props.measuredAt} stale={stale} />
    </span>
  );
}

/** Grey "nicht messbar" — the mono/muted text pair `ui/primitives.tsx` styles via CSS classes. */
export function NotMeasurable(props: { testId?: string }): ReactNode {
  const { t } = useLocale();
  return (
    <span data-testid={props.testId} className="mono muted">
      {t("admin.gtcc.cockpit.infra.not_measurable")}
    </span>
  );
}

/** The em dash the reference uses where a cell is simply empty. */
export function FleetDash(props: { testId?: string }): ReactNode {
  return (
    <span data-testid={props.testId} className="mono muted">
      &mdash;
    </span>
  );
}

const SPARK_WIDTH = 72;
const SPARK_HEIGHT = 20;

/** Fleet tones have a fifth ("muted") member the chart palette does not; charts render it as "info". */
function chartToneOf(tone: FleetTone): ChartTone {
  return tone === "muted" ? "info" : tone;
}

/**
 * Re-export of `ui/primitives.tsx`'s (`ui/charts.tsx`, really) `Sparkline`.
 * The point-scaling math has no equivalent there — `Sparkline` takes a
 * ready-made `points` string — so it stays here.
 */
export function FleetSparkline(props: {
  values: ReadonlyArray<number>;
  tone: FleetTone;
  dashed?: boolean;
  label: string;
  testId?: string;
}): ReactNode {
  const values = props.values.filter((value) => Number.isFinite(value));
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = SPARK_WIDTH / (values.length - 1);
  const points = values
    .map((value, index) => {
      const x = Math.round(index * step);
      const y = Math.round(SPARK_HEIGHT - ((value - min) / span) * (SPARK_HEIGHT - 4) - 2);
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <span data-testid={props.testId} data-dashed={props.dashed === true ? "true" : "false"}>
      <Sparkline points={points} tone={chartToneOf(props.tone)} dashed={props.dashed === true} />
    </span>
  );
}

export type MeterTone = "cool" | "violet" | "hot";

/**
 * Re-export of `ui/primitives.tsx`'s `Meter`. `Meter` requires a non-null
 * caption; a `null` caption here still means "no measurement", so it is
 * passed through as the empty string rather than invented text.
 */
export function FleetMeter(props: {
  percent: number;
  tone: MeterTone;
  caption: string | null;
  label: string;
  testId?: string;
}): ReactNode {
  return (
    <div data-testid={props.testId} data-meter={props.tone} aria-label={props.label}>
      <Meter tone={props.tone} pct={props.percent} caption={props.caption ?? ""} />
    </div>
  );
}

/**
 * Re-export of `ui/primitives.tsx`'s `DiskCell`. The forecast text and its
 * warn threshold ({@link FORECAST_WARN_DAYS}, {@link FORECAST_HORIZON_DAYS})
 * have no equivalent there — `DiskCell` takes ready-made label strings — so
 * that logic stays here.
 */
export function FleetDiskCell(props: { disk: FleetDiskDto; testId?: string }): ReactNode {
  const { t, locale } = useLocale();
  const { disk } = props;
  const days = disk.daysToFull;
  const warn = days !== null && days < FORECAST_WARN_DAYS;
  const forecast =
    days === null
      ? t("admin.gtcc.cockpit.infra.disk.forecast_unmeasurable")
      : days > FORECAST_HORIZON_DAYS
        ? t("admin.gtcc.cockpit.infra.disk.forecast_beyond", {
            days: formatInteger(FORECAST_HORIZON_DAYS, locale),
          })
        : t("admin.gtcc.cockpit.infra.disk.forecast", { days: formatInteger(days, locale) });
  return (
    <div data-testid={props.testId}>
      <DiskCell
        pct={disk.usedPercent}
        usedLabel={t("admin.gtcc.cockpit.infra.disk.used", {
          percent: formatWholePercent(disk.usedPercent, locale),
          bytes: formatDecimalGb(disk.usedBytes, locale),
        })}
        freeLabel={t("admin.gtcc.cockpit.infra.disk.free", {
          bytes: formatGib(disk.freeBytes, locale),
        })}
        forecast={forecast}
        hot={warn}
        {...(warn ? { forecastTone: "warn" as const } : {})}
      />
    </div>
  );
}

/** Re-export of `ui/primitives.tsx`'s `Row`. */
export function FleetRow(props: {
  tone: FleetTone;
  label: ReactNode;
  value: ReactNode;
  testId?: string;
}): ReactNode {
  return (
    <div data-testid={props.testId}>
      <Row tone={props.tone} value={props.value}>
        {props.label}
      </Row>
    </div>
  );
}

/** A muted note under the rows of a panel — the reference sets it in mono. */
export function FleetNote(props: { children: ReactNode; testId?: string }): ReactNode {
  return (
    <p data-testid={props.testId} className="mono muted" style={{ margin: 0 }}>
      {props.children}
    </p>
  );
}

/**
 * Same recipe as `GtccShell.tsx:137`. A GTCC table keeps the reference's
 * silent header cells (the dot column, the link column), and the repo's table
 * inventory demands that every column header carries a name and every table a
 * caption — so the name is rendered and hidden rather than left out.
 */
export const GTCC_VISUALLY_HIDDEN: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

export const PANEL_HEADING_STYLE: CSSProperties = {
  margin: 0,
  display: "flex",
  alignItems: "baseline",
  gap: "var(--gt-sys-space-2)",
  flexWrap: "wrap",
  fontSize: "var(--gt-sys-font-size-md)",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  color: "var(--gt-sys-text)",
};
